import {IController, ILogService, ITimeoutService} from "angular";
import {AccountsHttpService} from "../../../account/angular/services/accounts-http.service";
import {ServiceType} from "../../../deliveries/types/service.type";
import {EventFreqEnum} from "../../../commons/angular/enums/event-freq.enum";

export type VisitConfigurationCellEditField =
  'account' | 'callType' | 'destination' | 'startDate' | 'endDate' | 'frequency';

const FIELD_LABEL_KEYS: { [key in VisitConfigurationCellEditField]: string } = {
  account: 'Account',
  destination: 'Call Destination',
  startDate: 'Call Start Date',
  endDate: 'Call End Date',
  callType: 'Call Type',
  frequency: 'Call Frequency'
};

export class VisitConfigurationCellEditPanelComponent implements IController {
  field: VisitConfigurationCellEditField;
  rowNumber: number;

  public serviceTypeOptions: typeof ServiceType = ServiceType;
  public eventFreqOptions: typeof EventFreqEnum = EventFreqEnum;
  public fieldLabelKey: string;

  public pendingAccountId: string;
  public pendingAccountName: string;
  public isResolvingAccountName: boolean;
  public pendingAddress: any;
  public pendingDate: number;
  public pendingServiceType: string;
  public pendingFreq: string;
  public pendingWeekDay: string;
  public dayKeys: string[];
  public canSubmit: boolean;

  /* @ngInject */
  constructor(private mdPanelRef, private accountsHttpService: AccountsHttpService, private commonsConstants,
              private $timeout: ITimeoutService, private $log: ILogService) {
  }

  $onInit() {
    const data = this.mdPanelRef.config.locals.data;
    this.field = data.field;
    this.rowNumber = data.rowNumber;
    this.fieldLabelKey = FIELD_LABEL_KEYS[this.field];

    const value = data.value;

    switch (this.field) {
      case 'account':
        this.pendingAccountId = value?.id;
        this.pendingAccountName = value?.name;
        break;
      case 'destination':
        this.pendingAddress = value;
        break;
      case 'startDate':
      case 'endDate':
        this.pendingDate = value;
        break;
      case 'callType':
        this.pendingServiceType = value;
        break;
      case 'frequency':
        this.pendingFreq = value?.freq || EventFreqEnum.WEEKLY;
        this.dayKeys = Object.keys(this.commonsConstants.daysCheckboxConfig);
        this.pendingWeekDay = (value?.byWeekDay || [])[0];
        break;
    }

    this._updateCanSubmit();
  }

  onAccountChange(accountId) {
    this.pendingAccountId = accountId;
    this.pendingAccountName = undefined;
    this.isResolvingAccountName = !!accountId;
    this._updateCanSubmit();

    if (!accountId) {
      return;
    }

    this.accountsHttpService.getOne(accountId).subscribe({
      next: (account) => this._setResolvedAccountName(accountId, account?.name),
      error: (error) => {
        this.$log.error('Failed to resolve account name', error);
        this._setResolvedAccountName(accountId, undefined);
      }
    });
  }

  onAddressChange(address) {
    this.pendingAddress = address;
    this._updateCanSubmit();
  }

  onDateChange(selectedDate) {
    this.pendingDate = selectedDate;
    this._updateCanSubmit();
  }

  onSelectionChange() {
    this._updateCanSubmit();
  }

  private _setResolvedAccountName(accountId: string, name: string) {
    this.$timeout(() => {
      if (this.pendingAccountId !== accountId) {
        return;
      }
      this.pendingAccountName = name;
      this.isResolvingAccountName = false;
      this._updateCanSubmit();
    });
  }

  private _updateCanSubmit() {
    this.canSubmit = this._hasPendingValue();
  }

  private _hasPendingValue(): boolean {
    switch (this.field) {
      case 'account':
        return !!this.pendingAccountId && !this.isResolvingAccountName;
      case 'destination':
        return !!this.pendingAddress;
      case 'startDate':
      case 'endDate':
        return !!this.pendingDate;
      case 'callType':
        return !!this.pendingServiceType;
      case 'frequency':
        return this.pendingFreq === EventFreqEnum.DAILY ||
          (this.pendingFreq === EventFreqEnum.WEEKLY && !!this.pendingWeekDay);
      default:
        return false;
    }
  }

  cancel() {
    this.mdPanelRef.close();
  }

  submit() {
    if (!this.canSubmit) {
      return;
    }

    let result;
    switch (this.field) {
      case 'account':
        result = {id: this.pendingAccountId, name: this.pendingAccountName};
        break;
      case 'destination':
        result = this.pendingAddress;
        break;
      case 'startDate':
      case 'endDate':
        result = this.pendingDate;
        break;
      case 'callType':
        result = this.pendingServiceType;
        break;
      case 'frequency':
        result = {
          freq: this.pendingFreq,
          byWeekDay: this.pendingFreq === EventFreqEnum.WEEKLY && this.pendingWeekDay ? [this.pendingWeekDay] : []
        };
        break;
    }

    this.mdPanelRef.close().then(() => {
      this.mdPanelRef.config.locals.onSubmit(result);
    });
  }
}
